import { Modal } from '@lunary/ui-web/modal'
import { useState } from 'react'
import type { AdminUserResponseDto } from '@lunary/api-client'
import { ApiError } from '../../shared/api/client'
import { MergePreview } from './merge-preview'
import { MergeSearch } from './merge-search'
import { useMergePreview, useMergeUsers } from './queries'

type Props = {
  readonly open: boolean
  readonly user: AdminUserResponseDto
  readonly onClose: () => void
}

function errorText(error: Error | null): string | null {
  if (!error) return null
  return error instanceof ApiError ? error.message : 'Не получилось, попробуйте ещё раз'
}

export function MergeDialog({ open, user, onClose }: Props) {
  const [wasOpen, setWasOpen] = useState(false)
  const [sourceId, setSourceId] = useState<string | null>(null)
  const preview = useMergePreview(user.id, open ? sourceId : null)
  const merge = useMergeUsers(user.id)
  const resetMerge = merge.reset

  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setSourceId(null)
      resetMerge()
    }
  }

  const canMerge = preview.data !== undefined && preview.data.conflicts.length === 0
  const name = user.display_name ?? user.email ?? 'этот профиль'

  return (
    <Modal
      open={open}
      title="Объединить с другим аккаунтом"
      description={`Второй аккаунт будет удалён, а его способы входа и данные перейдут в профиль «${name}». Отменить это нельзя.`}
      dismissible={!merge.isPending}
      onClose={onClose}
      footer={
        <>
          {sourceId !== null && (
            <button type="button" className="secondary" disabled={merge.isPending} onClick={() => setSourceId(null)}>
              Назад
            </button>
          )}
          <button type="button" className="secondary" disabled={merge.isPending} onClick={onClose}>
            Отмена
          </button>
          {sourceId !== null && (
            <button
              type="button"
              className="danger"
              disabled={!canMerge || merge.isPending}
              onClick={() => merge.mutate(sourceId, { onSuccess: onClose })}
            >
              {merge.isPending ? 'Минуту…' : 'Объединить'}
            </button>
          )}
        </>
      }
    >
      {sourceId === null ? (
        <MergeSearch targetId={user.id} active={open} onPick={setSourceId} />
      ) : (
        <>
          {preview.isPending && <div className="spinner" aria-label="Загрузка" />}
          {preview.isError && (
            <p className="error-box" role="alert">
              {errorText(preview.error)}
            </p>
          )}
          {preview.data && <MergePreview preview={preview.data} />}
        </>
      )}
      {merge.error && (
        <p className="error-box" role="alert">
          {errorText(merge.error)}
        </p>
      )}
    </Modal>
  )
}
